import type { LocalRecord, TimestampReceipt } from './types';
import { toBase64 } from './base64';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export interface AuthSession {
  accessToken: string | null;
  role: 'participant' | 'operator' | null;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); this.name = 'ApiError'; }
}

async function api<T>(path: string, session: AuthSession, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (session.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`);
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers, cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { detail?: string };
    throw new ApiError(body.detail ?? `Ошибка сервера (${response.status})`, response.status);
  }
  return response.json() as Promise<T>;
}

export interface Submission {
  record_id: string;
  file_sha256: string;
  manifest_sha256: string;
  key_id: string;
  signature_b64: string;
  public_key_spki_b64: string;
}

export function submissionFor(record: LocalRecord): Submission {
  return {
    record_id: record.id,
    file_sha256: record.fileSha256,
    manifest_sha256: record.manifestSha256,
    key_id: record.keyId,
    signature_b64: toBase64(record.signature),
    public_key_spki_b64: toBase64(record.publicKeySpki),
  };
}

export const submitRecord = (value: Submission, session: AuthSession) => api<{ record_id: string; state: string }>('/records', session, { method: 'POST', body: JSON.stringify(value) });
export const getRecordState = (id: string, session: AuthSession) => api<{ state: string; timestamp?: TimestampReceipt; error?: string }>(`/records/${encodeURIComponent(id)}`, session);
export const createPublicCode = (id: string, session: AuthSession, hours=24) => api<{ code: string; expires_at:string }>(`/records/${encodeURIComponent(id)}/code`, session, { method: 'POST', body: JSON.stringify({expires_in_hours:hours}) });
export const revokePublicCode = (id:string,session:AuthSession) => api<{revoked:boolean}>(`/records/${encodeURIComponent(id)}/code`,session,{method:'DELETE'});
export const verifyPublicCode = (code: string) => api<{ state: string; file_sha256: string; timestamp_at?: string }>('/verify/code', { accessToken: null, role: null }, { method: 'POST', body: JSON.stringify({ code }) });
export const operatorStatus = (session: AuthSession) => api<{ version: string; tsa: string; queue: number; last_check_at: string | null; errors: number }>('/operator/status', session);
export const sessionRoles = (session: AuthSession) => api<{ roles: Array<'participant' | 'operator'> }>('/session', session);
export const submitExportStamp = (value: { export_id: string; record_id: string; export_sha256: string; key_id: string; signature_b64: string; public_key_spki_b64: string }, session: AuthSession) => api<{ export_id: string; state: string }>(`/exports/stamp`, session, { method: 'POST', body: JSON.stringify(value) });
export const getExportStamp = (id: string, session: AuthSession) => api<{ export_id: string; state: string; error?: string; timestamp?: TimestampReceipt }>(`/exports/${encodeURIComponent(id)}`, session);

export function authConfig() {
  return {
    authority: import.meta.env.VITE_OIDC_ISSUER ?? 'https://id.qdev.run',
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID ?? '',
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    scope: 'openid profile',
    automaticSilentRenew: true,
    monitorSession: false,
    loadUserInfo: false,
  };
}
